import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { RiBuilding2Fill } from 'react-icons/ri';
import * as rootStore from 'stores/root_store';
import { userPresenter, userStore } from 'stores/root_store';

import styles from './login.module.css';
import logoICRM from 'assets/icrm_logo.svg';
import icrmLogo from 'assets/bg_logo.svg';
import acLogo from 'assets/ac_logo.svg';
import isdLogo from 'assets/isd_logo.svg';
import vnIcon from 'assets/vn_icon.svg';
import ukIcon from 'assets/uk_icon.svg';

import { AiOutlineEyeInvisible, AiOutlineEye, AiOutlineLoading } from 'react-icons/ai';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TbLoaderQuarter } from 'react-icons/tb';
import { CommonModel } from 'services/Main/common_services';
import CryptoJS from 'crypto-js';
import { CRYPTED_KEY } from 'utils/constants';
import { Spin, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { decrypt, encrypt } from 'utils/cryptedHelper';

interface FormLoginModel {
  userName: string;
  password: string;
  saleOrg: string;
}

// validation foem bằng thư viện yup
const schema = yup.object().shape({
  userName: yup.string().required('Tên tài khoản không được để trống!'),
  password: yup.string().required('Mật khẩu không được để trống!'),
});

export function createLogin() {
  return observer(() => {
    //=====================================CHUYỂN NGÔN NGỮ=====================================//
    const { t } = useTranslation();

    // key lấy password, user name từ mvc
    const key = CryptoJS.enc.Utf8.parse(CRYPTED_KEY);

    const navigate = useNavigate();

    // Tên trang
    document.title = t('page-title.login');
    // ============= Lấy parameter đăng nhập ==================//
    // trang https://icrm.isdcorp.vn/ gửi user , password qua
    const [searchParams] = useSearchParams();
    const [selectSaleOrg, setSelectSaleOrg] = useState<string>('');

    // Nhớ thông tin đăng nhập
    const [isRemember, setIsRemember] = useState(false);

    // danh sach organization
    const [listOrg, setListOrg] = useState<CommonModel[]>([]);

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    // ========== Yup validate ===============
    const {
      register,
      handleSubmit,
      formState: { errors },
      setValue,
    } = useForm<FormLoginModel>({
      resolver: yupResolver(schema),
    });

    // =================== Show hide password =======================
    // hàm show và hide password. nếu đang show thì hide và ngược lại
    const handleShowPassword = () => {
      setShowPassword((pre) => !pre);
    };

    // ================== Submit login form =================
    const onLogin = async (value: FormLoginModel) => {
      setLoading(true);
      rootStore.uiPresenter.setLanguage(rootStore.uiStore, 'vi');
      await userPresenter.login(userStore, {
        userName: value.userName,
        password: value.password,
        saleOrg: selectSaleOrg,
        // dev thì lấy mặc định tiếng việt
        language: 'vi',
      });
      if (userStore.userRespone.isSuccess) {
        // Kiểm tra xem user có tick "Nhớ thông tin đăng nhập" không?
        if (isRemember) {
          // Nếu có thì lưu username và password vào localStorage và tự động móc ra lần sau
          localStorage.setItem('username', encrypt(value.userName));
          localStorage.setItem('password', encrypt(value.password));
          localStorage.setItem('saleOrg', encrypt(selectSaleOrg));
        } else {
          // Không thì cũng phải clear hết localStorage
          localStorage.removeItem('username');
          localStorage.removeItem('password');
          localStorage.removeItem('saleOrg');
        }
        navigate('/');
      } else {
        message.error(userStore.userRespone.message);
        // đăng nhập mvc thất bại nhưng vẫn cố nhảy qua fe thì fe nav ngược về lại màn đăng nhập của mvc
        if (searchParams.get('username')) {
          window.location.href = 'https://crm-rd-fe.isdcorp.vn/'
        }

      }
      setLoading(false);
    };

    // chọn company từ thẻ select
    const handleSelectOrg = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectSaleOrg(e.target.value);
    };

    useEffect(() => {
      const getListOrg = async () => {
        await rootStore.commonPresenter.getListSaleOrg(rootStore.commonStore);
        setListOrg(rootStore.commonStore.commonRespone.data);
      };
      // ghi nhớ mật khẩu
      const getRememberInfo = () => {
        const username = localStorage.getItem('username');
        const password = localStorage.getItem('password');
        const saleOrg = localStorage.getItem('saleOrg');

        // có thông tin thì set vào form và show lên
        if (username && password && saleOrg) {
          setValue('userName', decrypt(username));
          setValue('password', decrypt(password));
          setValue('saleOrg', decrypt(saleOrg));
          setSelectSaleOrg(decrypt(saleOrg));
          setIsRemember(true);
        } else {
          // Không thì cũng phải clear hết localStorage
          localStorage.removeItem('username');
          localStorage.removeItem('password');
          localStorage.removeItem('saleOrg');
        }
      };

      // đăng nhập từ mvc
      const LoginHasStringParams = async () => {
        setLoading(true);
        if (searchParams.get('username')) {
          await userPresenter.login(userStore, {
            userName: decrypted(searchParams.get('username')),
            password: decrypted(searchParams.get('password')),
            saleOrg: searchParams.get('plantCode'),
            language: searchParams.get('language'),
          });

          localStorage.setItem('username', encrypt(decrypted(searchParams.get('username'))));
          localStorage.setItem('password', encrypt(decrypted(searchParams.get('password'))));
          localStorage.setItem('saleOrg', encrypt(searchParams.get('plantCode') || ''));

          rootStore.uiPresenter.setLanguage(rootStore.uiStore, searchParams.get('language') || 'vi');
          if (userStore.userRespone.isSuccess) {
            navigate('/');
          } else {
            message.error(userStore.userRespone.message);
          }
        }
        setLoading(false);
      };

      LoginHasStringParams().then(() => {
        getListOrg().then(() => {
          // chạy xong getListOrg() thì mới được call info login
          getRememberInfo();
        });
      });
    }, []);

    // Hàm giải mã user passwword từ mvc
    const decrypted = (data: any) => {
      const decryptedData = CryptoJS.AES.decrypt(data, key, {
        keySize: 256 / 8,
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      });
      return CryptoJS.enc.Utf8.stringify(decryptedData);
    };

    const antIcon = <LoadingOutlined style={{ fontSize: 45, color: 'white' }} spin />;
    return (
      <div className={styles.wrapper}>
        {process.env.NODE_ENV === 'production' ? (
          <div className="h-screen w-full flex items-center justify-center">
            <Spin indicator={antIcon} />
          </div>
        ) : (
          <div className={`bg-[#1F2D47] flex ${styles.containerWidth}`}>
            <div className={`relative w-1/2 hidden xl:flex md:flex sm:hiden flex-col justify-center items-center p-5`}>
              <div className="flex justify-center">
                <img src={icrmLogo} className="w-[80%]" />
              </div>
            </div>
            <div>
              <img src={acLogo} className="h-[39px] absolute top-[42px] left-[21px] sm:left-[165px]" />
              <img src={isdLogo} className="h-[39px] absolute top-[42px] right-[21px] sm:right-[930px]" />
            </div>
            <div className="xl:w-1/2 md:w-1/2 w-full p-[5%] sm:p-[10%]">
              <div className="w-full flex justify-between mb-6 items-end"></div>
              <div>
                <img src={vnIcon} className="h-[25px] absolute top-[110px] sm:top-[38px] right-[85px]" />
                <img src={ukIcon} className="h-[25px] absolute top-[110px] sm:top-[38px] right-[30px]" />
              </div>
              <div className="flex justify-center mb-2.5 sm:hidden">
                <img src={logoICRM} className="h-[40px]" />
              </div>
              <form onSubmit={handleSubmit(onLogin)} className="space-y-4">
                <h3 className="text-3xl font-bold text-white mb-8 text-center lg:text-left">Login system</h3>
                <div className="relative text-gray-500">
                  <input
                    {...register('userName')}
                    type="text"
                    id="userName"
                    placeholder="Enter your email"
                    autoComplete="off"
                    className="peer w-full border-2 border-gray-200 py-2 pr-3 pl-4 bg-transparent outline-none placeholder-slate-400 focus:border-blue-300 h-[40px] rounded-sm text-white"
                  />
                </div>
                {/* thông báo lỗi khi userName không hợp lệ */}
                {errors.userName && <p className="!mt-1 text-xs text-red-500 pl-5">{`${errors.userName.message}`}</p>}
                <div className="relative text-gray-500">
                  <input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    placeholder="Password"
                    autoComplete="off"
                    className="peer w-full border-2 border-gray-200 py-2 pr-12 pl-4 bg-transparent outline-none placeholder-slate-400 focus:border-blue-300 h-[40px] rounded-sm text-white"
                  />
                  {/* set icon cho sự kiện show và hide password */}
                  {showPassword ? (
                    <button type="button">
                      <AiOutlineEye
                        onClick={handleShowPassword}
                        className="peer-focus:text-blue-400 absolute right-0 top-1/2 translate-y-[-50%] mx-5 text-xl cursor-pointer text-gray-400"
                      />
                    </button>
                  ) : (
                    <button type="button">
                      <AiOutlineEyeInvisible
                        onClick={handleShowPassword}
                        className="peer-focus:text-blue-400 absolute right-0 top-1/2 translate-y-[-50%] mx-5 text-xl cursor-pointer text-gray-400"
                      />
                    </button>
                  )}
                </div>
                {/* thông báo lỗi khi password không hợp lệ */}
                {errors.password && <p className="!mt-1 text-xs text-red-500 pl-5">{`${errors.password.message}`}</p>}
                <div className="text-gray-500 relative w-full  border-2 border-gray-200 items-center rounded-sm sm:flex overflow-hidden">
                  {/* <RiBuilding2Fill className="absolute mx-5 text-gray-400" /> */}
                  <select
                    {...register('saleOrg')}
                    onChange={handleSelectOrg}
                    className="bg-[#1F2D47] text-white w-full py-2 pl-4 mr-3 focus-visible:outline-none h-[40px]"
                    value={selectSaleOrg}
                  >
                    <option value={''}>-- Công ty --</option>
                    {listOrg.map((item, index) => {
                      return (
                        <option key={index} value={item.key as string}>
                          {item.value}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="w-full flex items-center">
                  <input
                    type="checkbox"
                    className={styles.checkBox}
                    id="btnRemember"
                    onChange={() => setIsRemember(!isRemember)}
                    checked={isRemember}
                  />
                  <label htmlFor="btnRemember">
                    <h3 className="text-white text-sm font-medium ml-2 p-0 mb-0 cursor-pointer select-none">
                      Remember me
                    </h3>
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full text-white font-medium rounded-sm text-sm px-5 py-2.5 mt-[30px] text-center focus:outline-none h-[40px] ${loading ? '!bg-[#E3EDF6]' : '!bg-[#E3EDF6] hover:bg-[#E3EDF6]/75'
                    }`}
                >
                  {loading ? (
                    <span className="text-[#354A5F] font-bold flex items-center justify-center">
                      <TbLoaderQuarter className="animate-spin bg mr-2" />
                      {t('btn.login')}...
                    </span>
                  ) : (
                    <span className="text-[#354A5F] font-bold">{t('btn.login')}</span>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  });
}
